import { api } from '../../../services/api';

/* `api.get` is `apiRequest.bind(null, 'GET')`, and Function.prototype.bind
   drops the original signature's generic parameter — so `api.get<T>(url)` does
   not type-check here. Every call is cast through ApiResult<T> instead, the
   same convention used in pages/emerge/emergeApi.ts. */
export interface ApiResult<T> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

export interface Reason { code: string; label: string; detail?: string }
export interface Risk { level: 'Low' | 'Moderate' | 'High' | 'Critical'; score: number; reasons: Reason[] }

export type Health = 'ON TRACK' | 'WATCH' | 'AT RISK' | 'BLOCKED';
export type Confidence = 'ON TRACK' | 'AT RISK' | 'UNLIKELY' | 'BREACHED';

export interface Metrics {
  workstreams: number;
  tasksTotal: number;
  tasksOpen: number;
  tasksDone: number;
  atRiskWorkstreams: number;
  blockedWorkstreams: number;
  overdue: number;
  overdueCritical: number;
  blocked: number;
  milestonesBreached: number;
  milestonesDueThisWeek: number;
  decisionsRequired: number;
  commitmentsOpen: number;
  commitmentsMissed: number;
  missingNextAction: number;
  effortDaysRemaining: number;
}

export interface ChainLink {
  id: string;
  name: string;
  cleanName: string;
  ownerRole: string | null;
  dueDate: string | null;
  percentComplete: number;
  status: string | null;
  isDone: boolean;
  daysRemaining: number | null;
  confidence: Confidence;
  reasons: Reason[];
}

export interface Chain {
  links: ChainLink[];
  terminal: ChainLink | null;
  breachedCount: number;
  confidence: Confidence;
}

export interface WorkstreamState {
  id: string;
  key: string;
  name: string;
  executiveOwner: string | null;
  targetDate: string | null;
  health: Health;
  healthReasons: Reason[];
  stats: {
    total: number; open: number; done: number; overdue: number;
    overdueCritical: number; blocked: number; milestones: number;
    breachedMilestones: number; percentComplete: number;
  };
  criticalMilestone: { id: string; name: string; dueDate: string | null; percentComplete: number } | null;
  primaryRisk: { id: string; name: string; level: Risk['level']; reasons: Reason[] } | null;
}

export interface Summary {
  metrics: Metrics;
  workstreams: WorkstreamState[];
  criticalPath: Chain;
  freshness: { source: string; label: string | null; importedAt: string; rows: number } | null;
}

export interface AttentionItem {
  kind: 'DECISION_REQUIRED' | 'BLOCKED' | 'MILESTONE_BREACH' | 'CRITICAL_DEADLINE';
  id: string;
  title: string;
  workstream: string | null;
  owner: string | null;
  impact: string | null;
  recommended?: string | null;
  deadline: string | null;
  reasons?: Reason[];
  score: number;
}

export interface Task {
  id: string;
  name: string;
  workstream: string | null;
  workstreamId: string;
  phase: string | null;
  owner: string | null;
  status: string | null;
  statusNormalized: string;
  priority: string | null;
  priorityNormalized: string;
  startDate: string | null;
  dueDate: string | null;
  percentComplete: number;
  effortDays: number | null;
  notes: string | null;
  isMilestone: boolean;
  criticalPath: boolean;
  blocked: boolean;
  blockerReason: string | null;
  nextAction: string | null;
  nextActionOwner: string | null;
  nextActionDue: string | null;
  advancementCriterion: string | null;
  strategicValue: string | null;
  economicValue: string | null;
  origin: string;
  externalSource: string;
  risk: Risk;
}

export interface WorkstreamDetail extends Omit<WorkstreamState, 'criticalMilestone' | 'primaryRisk'> {
  terminalOutcome: string | null;
  chain: Chain;
  tasks: Task[];
  dependencies: Array<{
    id: string; task_id: string; task_name: string;
    depends_on_name: string | null; depends_on_status: string | null;
    confidence: string; source_text: string | null; unresolved_label: string | null;
  }>;
  commitments: Commitment[];
  decisions: Decision[];
}

export interface Commitment {
  id: string;
  commitment: string;
  owner_role: string | null;
  workstream_name: string | null;
  task_name: string | null;
  date_committed: string | null;
  due_date: string | null;
  status: 'OPEN' | 'FULFILLED' | 'MISSED' | 'RENEGOTIATED';
  evidence: string | null;
  source: string;
}

export interface Decision {
  id: string;
  title: string;
  context: string | null;
  business_impact: string | null;
  required_of_role: string | null;
  recommended_decision: string | null;
  decision_deadline: string | null;
  status: 'OPEN' | 'DECIDED' | 'DEFERRED';
  workstream_name: string | null;
}

export interface TaskDetail extends Task {
  phaseId: string | null;
  events: ActivityEvent[];
  dependsOn: Array<{
    id: string; depends_on_name: string | null; depends_on_status: string | null;
    confidence: string; source_text: string | null; unresolved_label: string | null;
  }>;
  blocks: Array<{ id: string; task_name: string; task_status: string }>;
  commitments: Commitment[];
}

export interface Phase {
  id: string;
  name: string;
  workstream_id: string;
  workstream_name: string;
  marker: string | null;
  is_critical_path: boolean;
}

export interface ActivityEvent {
  id: string;
  task_name: string | null;
  workstream_name: string | null;
  field: string;
  before_value: string | null;
  after_value: string | null;
  change_kind: string | null;
  source: string;
  changed_at: string;
}

export interface WeeklyReviewData {
  weekEnding: string;
  isBaseline: boolean;
  comparedTo: string | null;
  metrics: Metrics;
  executiveSummary: {
    headline: Confidence;
    atRisk: number;
    overdueCritical: number;
    decisionsRequired: number;
    terminalMilestone: ChainLink | null;
  };
  wins: Array<{ id: string; name: string; workstream: string | null; owner: string | null }>;
  missedCommitments: Commitment[];
  overdueOpenCommitments: Commitment[];
  newBlockers: Array<{ id: string; name: string; workstream: string | null; owner: string | null; reason: string | null }>;
  newlyOverdue: Array<{
    id: string; name: string; workstream: string | null; owner: string | null;
    dueDate: string | null; percentComplete: number; risk: Risk;
  }>;
  healthChanges: Array<{ id: string; name: string; from: Health; to: Health }>;
  criticalPath: Chain;
  decisionsPending: Decision[];
  topRisks: Array<{
    id: string; name: string; workstream: string | null; owner: string | null;
    dueDate: string | null; level: Risk['level']; reasons: Reason[];
  }>;
  departmentSummaries: Array<{
    name: string; health: Health; owner: string | null;
    open: number; overdue: number; blocked: number;
    nextMilestone: { name: string; dueDate: string | null; percentComplete: number } | null;
  }>;
  nextWeekCommitments: Commitment[];
  freshness: unknown;
}

const qs = (params: Record<string, string | boolean | number | undefined>) => {
  const p = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== '' && v !== false) p.set(k, String(v));
  });
  const s = p.toString();
  return s ? `?${s}` : '';
};

export const pmApi = {
  getSummary: async (): Promise<ApiResult<Summary>> =>
    (await api.get('/pm/summary')) as ApiResult<Summary>,

  getWorkstreams: async (): Promise<ApiResult<WorkstreamState[]>> =>
    (await api.get('/pm/workstreams')) as ApiResult<WorkstreamState[]>,

  getWorkstream: async (id: string): Promise<ApiResult<WorkstreamDetail>> =>
    (await api.get(`/pm/workstreams/${id}`)) as ApiResult<WorkstreamDetail>,

  getCriticalPath: async (): Promise<ApiResult<Chain>> =>
    (await api.get('/pm/critical-path')) as ApiResult<Chain>,

  getAttention: async (): Promise<ApiResult<AttentionItem[]>> =>
    (await api.get('/pm/attention')) as ApiResult<AttentionItem[]>,

  getTasks: async (filters: Record<string, string | boolean | number | undefined> = {}): Promise<ApiResult<Task[]>> =>
    (await api.get(`/pm/tasks${qs(filters)}`)) as ApiResult<Task[]>,

  getTask: async (id: string): Promise<ApiResult<TaskDetail>> =>
    (await api.get(`/pm/tasks/${id}`)) as ApiResult<TaskDetail>,

  createTask: async (body: Record<string, unknown>): Promise<ApiResult<Task>> =>
    (await api.post('/pm/tasks', body)) as ApiResult<Task>,

  updateTask: async (id: string, body: Record<string, unknown>): Promise<ApiResult<unknown>> =>
    (await api.patch(`/pm/tasks/${id}`, body)) as ApiResult<unknown>,

  deleteTask: async (id: string): Promise<ApiResult<{ id: string }>> =>
    (await api.delete(`/pm/tasks/${id}`)) as ApiResult<{ id: string }>,

  getPhases: async (workstreamId?: string): Promise<ApiResult<Phase[]>> =>
    (await api.get(`/pm/phases${qs({ workstreamId })}`)) as ApiResult<Phase[]>,

  getCommitments: async (status?: string): Promise<ApiResult<Commitment[]>> =>
    (await api.get(`/pm/commitments${qs({ status })}`)) as ApiResult<Commitment[]>,

  createCommitment: async (body: Record<string, unknown>): Promise<ApiResult<Commitment>> =>
    (await api.post('/pm/commitments', body)) as ApiResult<Commitment>,

  updateCommitment: async (id: string, body: Record<string, unknown>): Promise<ApiResult<Commitment>> =>
    (await api.patch(`/pm/commitments/${id}`, body)) as ApiResult<Commitment>,

  getDecisions: async (status?: string): Promise<ApiResult<Decision[]>> =>
    (await api.get(`/pm/decisions${qs({ status })}`)) as ApiResult<Decision[]>,

  createDecision: async (body: Record<string, unknown>): Promise<ApiResult<Decision>> =>
    (await api.post('/pm/decisions', body)) as ApiResult<Decision>,

  updateDecision: async (id: string, body: Record<string, unknown>): Promise<ApiResult<Decision>> =>
    (await api.patch(`/pm/decisions/${id}`, body)) as ApiResult<Decision>,

  getWeeklyReview: async (): Promise<ApiResult<WeeklyReviewData>> =>
    (await api.get('/pm/weekly-review')) as ApiResult<WeeklyReviewData>,

  snapshotWeeklyReview: async (notes?: string): Promise<ApiResult<{ id: string; weekEnding: string }>> =>
    (await api.post('/pm/weekly-review/snapshot', { notes })) as ApiResult<{ id: string; weekEnding: string }>,

  getOwners: async (): Promise<ApiResult<Array<{ id: string; role_code: string; is_executive: boolean }>>> =>
    (await api.get('/pm/owners')) as ApiResult<Array<{ id: string; role_code: string; is_executive: boolean }>>,

  getActivity: async (limit = 20): Promise<ApiResult<ActivityEvent[]>> =>
    (await api.get(`/pm/activity${qs({ limit })}`)) as ApiResult<ActivityEvent[]>,
};
